import * as DialogPrimitive from '@radix-ui/react-dialog';
import {
  AlertTriangle,
  CheckCircle2,
  Info,
  X,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import {
  forwardRef,
  useEffect,
  useState,
  type ComponentPropsWithoutRef,
  type ElementRef,
  type HTMLAttributes,
  type ReactNode,
} from 'react';
import { Button } from './button';
import { Textarea } from './textarea';
import { getIconTone, getSectionBadgeClasses } from './action-colors';
import { cn } from './utils';
import { portalContainment } from './portal-containment';

/* -------------------------------------------------------------------------- */
/*  Primitives                                                                */
/* -------------------------------------------------------------------------- */

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogPortal = DialogPrimitive.Portal;

/* -------------------------------------------------------------------------- */
/*  Overlay                                                                   */
/* -------------------------------------------------------------------------- */

export const DialogOverlay = forwardRef<
  ElementRef<typeof DialogPrimitive.Overlay>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-black/50 backdrop-blur-sm',
      'data-[state=open]:animate-in data-[state=closed]:animate-out',
      'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = 'DialogOverlay';

/* -------------------------------------------------------------------------- */
/*  Content                                                                   */
/* -------------------------------------------------------------------------- */

export type DialogMaxWidth = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | 'full';

// Widened baseline — every size steps up so modals actually use the space
// they need. Previously `lg` was 512px which crammed line-item tables into
// a scroll box; now `lg` is 960px, which is what a "New delivery / New
// order" form actually needs. Individual dialogs can still opt into `sm`
// for a small confirm, or `full` for very wide grids.
const maxWidthClasses: Record<DialogMaxWidth, string> = {
  sm: 'max-w-[480px]',
  md: 'max-w-[720px]',
  lg: 'max-w-[960px]',
  xl: 'max-w-[1140px]',
  '2xl': 'max-w-[1280px]',
  '3xl': 'max-w-[1440px]',
  full: 'max-w-[calc(100vw-2rem)]',
};

export interface DialogContentProps
  extends ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  /** Controls the panel width. Defaults to `lg` (max-w-lg). */
  maxWidth?: DialogMaxWidth;
  /** Show the top-right close button. Defaults to `true`. */
  showCloseButton?: boolean;
}

export const DialogContent = forwardRef<
  ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(
  (
    { className, children, maxWidth = 'lg', showCloseButton = true, onClick, onKeyDown, ...props },
    ref,
  ) => (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        {...portalContainment(onClick, onKeyDown)}
        className={cn(
          // Position — centered
          'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
          // Sizing
          'w-full',
          maxWidthClasses[maxWidth],
          // Never taller than the viewport. Without this the panel was
          // header + 70vh body + footer, which exceeds 100vh on any screen
          // under ~470px tall — a phone in landscape, or any phone with the
          // keyboard open. Being vertically centred, it overflowed BOTH ends,
          // so the title and the Save/Cancel buttons were cut off with no way
          // to scroll to them. dvh so the mobile URL bar is accounted for.
          'max-h-[calc(100dvh-2rem)] flex flex-col',
          // Surface — token-driven
          'bg-card text-foreground',
          'border border-border shadow-lg rounded-xl',
          'p-0',
          // Focus outline suppression — Radix will focus the panel on open
          'focus:outline-none',
          // Animation
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
          'data-[state=open]:slide-in-from-bottom-2 data-[state=closed]:slide-out-to-bottom-2',
          'duration-200',
          className,
        )}
        {...props}
      >
        {children}
        {showCloseButton ? <DialogClose /> : null}
      </DialogPrimitive.Content>
    </DialogPortal>
  ),
);
DialogContent.displayName = 'DialogContent';

/* -------------------------------------------------------------------------- */
/*  Close button (top-right X)                                                */
/* -------------------------------------------------------------------------- */

export interface DialogCloseProps
  extends ComponentPropsWithoutRef<typeof DialogPrimitive.Close> {
  /** Accessible label for screen readers. */
  srLabel?: string;
}

export const DialogClose = forwardRef<
  ElementRef<typeof DialogPrimitive.Close>,
  DialogCloseProps
>(({ className, srLabel = 'Close', ...props }, ref) => (
  <DialogPrimitive.Close
    ref={ref}
    className={cn(
      'absolute right-4 top-4 inline-flex h-8 w-8 items-center justify-center',
      'rounded-lg text-muted-foreground',
      'transition-colors hover:bg-accent hover:text-foreground',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
      'focus-visible:ring-offset-2 focus-visible:ring-offset-background',
      'disabled:pointer-events-none',
      className,
    )}
    {...props}
  >
    <X className="h-4 w-4" aria-hidden />
    <span className="sr-only">{srLabel}</span>
  </DialogPrimitive.Close>
));
DialogClose.displayName = 'DialogClose';

/* -------------------------------------------------------------------------- */
/*  Header / Title / Description / Body / Footer                              */
/* -------------------------------------------------------------------------- */

export const DialogHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'flex flex-col space-y-1 px-6 py-4 border-b border-border',
        // Keep room for the close button on the right.
        'pr-14',
        className,
      )}
      {...props}
    />
  ),
);
DialogHeader.displayName = 'DialogHeader';

export const DialogTitle = forwardRef<
  ElementRef<typeof DialogPrimitive.Title>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-base font-semibold text-foreground leading-6', className)}
    {...props}
  />
));
DialogTitle.displayName = 'DialogTitle';

export const DialogDescription = forwardRef<
  ElementRef<typeof DialogPrimitive.Description>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('mt-1 text-[13px] text-muted-foreground', className)}
    {...props}
  />
));
DialogDescription.displayName = 'DialogDescription';

export const DialogBody = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        // min-h-0 lets this shrink inside the flex column; without it a
        // flex child refuses to go below its content height and the panel
        // grows past max-h again.
        'px-6 py-4 min-h-0 flex-1 overflow-y-auto text-sm text-foreground',
        className,
      )}
      {...props}
    />
  ),
);
DialogBody.displayName = 'DialogBody';

export const DialogFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'flex flex-col-reverse gap-2 px-6 py-4 border-t border-border bg-muted/20',
        'sm:flex-row sm:justify-end sm:gap-2 rounded-b-xl',
        className,
      )}
      {...props}
    />
  ),
);
DialogFooter.displayName = 'DialogFooter';

/* -------------------------------------------------------------------------- */
/*  ConfirmDialog                                                             */
/* -------------------------------------------------------------------------- */

export type ConfirmTone = 'positive' | 'destructive' | 'warning' | 'info';

const DEFAULT_TONE_ICONS: Record<ConfirmTone, LucideIcon> = {
  positive: CheckCircle2,
  destructive: XCircle,
  warning: AlertTriangle,
  info: Info,
};

/** Map a ConfirmTone to the Button variant we render on the confirm CTA. */
function confirmButtonVariantForTone(
  tone: ConfirmTone,
): NonNullable<import('./button').ButtonProps['variant']> {
  switch (tone) {
    case 'destructive':
      // Filled destructive — the modal is already a "you are about to do
      // something irreversible" moment, so the CTA earns a bold red.
      return 'destructive';
    case 'positive':
      // Soft emerald outline keeps the CTA obvious without shouting.
      return 'positive';
    case 'warning':
      return 'softWarning';
    case 'info':
      return 'info';
  }
}

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /**
   * Fires after the user clicks the confirm CTA. When `reasonRequired` is set,
   * the reason string is passed as the second argument.
   */
  onConfirm: (reason?: string) => void | Promise<void>;
  /** Legacy back-compat — `'danger'` aliases to `tone='destructive'`. */
  variant?: 'default' | 'danger';
  /**
   * Semantic tone of the action being confirmed. Controls the header icon +
   * confirm-button variant. Falls back to the `variant` prop when omitted.
   */
  tone?: ConfirmTone;
  /** Explicit Lucide icon override. Defaults derive from `tone`. */
  icon?: LucideIcon;
  /**
   * When true, renders a Textarea in the body and blocks confirm until it has
   * non-whitespace content. The trimmed value is passed to `onConfirm`.
   */
  reasonRequired?: boolean;
  /** Label above the reason Textarea. Defaults to "Reason". */
  reasonLabel?: string;
  /** Small monospace descriptor chip below the title — e.g. "PO-JAYCO-0042". */
  entityDescriptor?: string;
  loading?: boolean;
  /** Optional max-width override for the underlying panel. */
  maxWidth?: DialogMaxWidth;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  variant = 'default',
  tone,
  icon,
  reasonRequired = false,
  reasonLabel = 'Reason',
  entityDescriptor,
  loading = false,
  maxWidth = 'md',
}: ConfirmDialogProps) {
  // Resolve tone. Explicit `tone` wins; else fall back to `variant`.
  const resolvedTone: ConfirmTone | null =
    tone ?? (variant === 'danger' ? 'destructive' : null);
  const Icon: LucideIcon | null =
    icon ?? (resolvedTone ? DEFAULT_TONE_ICONS[resolvedTone] : null);

  const [reason, setReason] = useState('');

  // Reset the reason each time the dialog opens so a stale value doesn't leak
  // between two consecutive confirms of the same action.
  useEffect(() => {
    if (open) setReason('');
  }, [open]);

  const reasonValid = !reasonRequired || reason.trim().length > 0;

  const handleConfirm = async () => {
    if (!reasonValid) return;
    await onConfirm(reasonRequired ? reason.trim() : undefined);
  };

  const handleCancel = () => {
    if (loading) return;
    onOpenChange(false);
  };

  const confirmVariant = resolvedTone
    ? confirmButtonVariantForTone(resolvedTone)
    : 'default';

  const iconBadgeClasses = resolvedTone
    ? getSectionBadgeClasses(resolvedTone)
    : 'bg-primary/10 text-primary';
  const iconColorClass = resolvedTone ? getIconTone(resolvedTone) : 'text-primary';

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (loading && !next) return;
        onOpenChange(next);
      }}
    >
      <DialogContent maxWidth={maxWidth}>
        <DialogHeader>
          <div className="flex items-start gap-3">
            {Icon ? (
              <div
                className={cn(
                  'mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
                  iconBadgeClasses,
                )}
                aria-hidden
              >
                <Icon className={cn('h-5 w-5', iconColorClass)} />
              </div>
            ) : null}
            <div className="min-w-0 flex-1">
              <DialogTitle>{title}</DialogTitle>
              {entityDescriptor ? (
                <div className="mt-1">
                  <span className="inline-block rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground">
                    {entityDescriptor}
                  </span>
                </div>
              ) : null}
              {description ? (
                <DialogDescription>{description}</DialogDescription>
              ) : null}
            </div>
          </div>
        </DialogHeader>
        {reasonRequired ? (
          <DialogBody className="max-h-none overflow-visible">
            <label className="mb-1 block text-[13px] font-medium text-foreground">
              {reasonLabel}
              <span className="ml-0.5 text-destructive" aria-hidden>
                *
              </span>
            </label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              autoResize
              placeholder="Enter a short reason…"
              disabled={loading}
            />
          </DialogBody>
        ) : null}
        <DialogFooter>
          <Button variant="outline" onClick={handleCancel} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button
            variant={confirmVariant}
            onClick={handleConfirm}
            loading={loading}
            disabled={loading || !reasonValid}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* -------------------------------------------------------------------------- */
/*  DrawerDialog — side-panel variant built on Radix Dialog                   */
/* -------------------------------------------------------------------------- */

export type DrawerSide = 'left' | 'right' | 'top' | 'bottom';
export type DrawerSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

const drawerSizeClasses: Record<DrawerSide, Record<DrawerSize, string>> = {
  left: {
    sm: 'w-full max-w-xs',
    md: 'w-full max-w-sm',
    lg: 'w-full max-w-md',
    xl: 'w-full max-w-lg',
    full: 'w-screen',
  },
  right: {
    sm: 'w-full max-w-xs',
    md: 'w-full max-w-sm',
    lg: 'w-full max-w-md',
    xl: 'w-full max-w-lg',
    full: 'w-screen',
  },
  top: {
    sm: 'h-1/4',
    md: 'h-1/3',
    lg: 'h-1/2',
    xl: 'h-2/3',
    full: 'h-screen',
  },
  bottom: {
    sm: 'h-1/4',
    md: 'h-1/3',
    lg: 'h-1/2',
    xl: 'h-2/3',
    full: 'h-screen',
  },
};

const drawerSideClasses: Record<DrawerSide, string> = {
  left: 'inset-y-0 left-0 h-full border-r rounded-r-xl',
  right: 'inset-y-0 right-0 h-full border-l rounded-l-xl',
  top: 'inset-x-0 top-0 w-full border-b rounded-b-xl',
  bottom: 'inset-x-0 bottom-0 w-full border-t rounded-t-xl',
};

const drawerAnimationClasses: Record<DrawerSide, string> = {
  left: 'data-[state=open]:slide-in-from-left data-[state=closed]:slide-out-to-left',
  right:
    'data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right',
  top: 'data-[state=open]:slide-in-from-top data-[state=closed]:slide-out-to-top',
  bottom:
    'data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom',
};

export interface DrawerDialogProps
  extends ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  /** Side of the viewport the drawer slides in from. Defaults to `right`. */
  side?: DrawerSide;
  /** Drawer size on the cross axis (width for left/right, height for top/bottom). */
  size?: DrawerSize;
  /** Show the top-right close button. Defaults to `true`. */
  showCloseButton?: boolean;
}

export const DrawerDialog = forwardRef<
  ElementRef<typeof DialogPrimitive.Content>,
  DrawerDialogProps
>(
  (
    {
      className,
      children,
      side = 'right',
      size = 'md',
      showCloseButton = true,
      onClick,
      onKeyDown,
      ...props
    },
    ref,
  ) => (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          'fixed z-50 flex flex-col',
          'bg-card text-foreground',
          'border-border shadow-lg',
          'focus:outline-none',
          drawerSideClasses[side],
          drawerSizeClasses[side][size],
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
          drawerAnimationClasses[side],
          'duration-200',
          className,
        )}
        {...portalContainment(onClick, onKeyDown)}
        {...props}
      >
        {children}
        {showCloseButton ? <DialogClose /> : null}
      </DialogPrimitive.Content>
    </DialogPortal>
  ),
);
DrawerDialog.displayName = 'DrawerDialog';
